import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { effectScope } from 'vue';
import { useAuth } from '@/domains/auth';
import { useStoredUserGuid } from '@/composables/useStoredUserGuid';
import { formatPageTitle } from '@/config/environment';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/common/Home.vue'),
    meta: {
      title: 'Home',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/auth/verify/:uuid',
    name: 'auth-verify',
    component: () => import('@/views/auth/OtpVerificationView.vue'),
    props: true,
    meta: {
      title: 'OTP Verification',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/auth/login/:uuid',
    name: 'auth-check',
    component: () => import('@/views/auth/AuthCheckView.vue'),
    props: true,
    meta: {
      title: 'Authentication Check',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/route-list',
    name: 'route-list',
    component: () => import('@/views/routes/RouteListView.vue'),
    meta: {
      title: 'Route List',
      layout: 'DefaultLayout',
      requiresAuth: true,
      backNavigation: '/',
    },
  },
  {
    path: '/route-details/:id',
    name: 'route-summary',
    component: () => import('@/views/routes/RouteSummaryView.vue'),
    props: true,
    meta: {
      title: 'Route Summary',
      layout: 'DefaultLayout',
      requiresAuth: true,
      backNavigation: '/route-list',
    },
  },
  {
    path: '/opt-in',
    name: 'opt-in',
    component: () => import('@/views/optin/OptInView.vue'),
    meta: {
      title: 'Opt In',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/opt-in/:uuid',
    name: 'opt-in-with-uuid',
    component: () => import('@/views/optin/OptInView.vue'),
    props: true,
    meta: {
      title: 'Opt In',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/validate/:uuid',
    name: 'validate',
    component: () => import('@/views/optin/PhoneValidationView.vue'),
    props: true,
    meta: {
      title: 'Validate Phone',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/success',
    name: 'success',
    component: () => import('@/views/optin/Success.vue'),
    meta: {
      title: 'Success',
      layout: 'BlankLayout',
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/optin/PrivacyPolicy.vue'),
    meta: {
      title: 'Privacy Policy',
      layout: 'DefaultLayout',
    },
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import('@/views/optin/TermsOfService.vue'),
    meta: {
      title: 'Terms of Service',
      layout: 'DefaultLayout',
    },
  },
  {
    path: '/version',
    name: 'version',
    component: () => import('@/views/common/VersionView.vue'),
    meta: {
      title: 'Version',
      layout: 'DefaultLayout',
    },
  },
  {
    path: '/route-stop-scans/:id/shipment',
    name: 'route-stop-scans',
    component: () => import('@/views/routes/RouteStopScan.vue'),
    props: true,
    meta: {
      title: 'Route Stop Scans',
      requiresAuth: true,
      backNavigation: '/route-details',
    },
  },
  {
    // Accepts an entity type and an ID
    path: '/device-images/:deviceImageTypeId/:entityId',
    name: 'ImageCapture',
    component: () => import('@/views/deviceImages/DeviceImagesView.vue'),
    props: (route) => {
      const redirectTo = route.query.redirectTo;

      if (typeof redirectTo !== 'string' || !redirectTo.trim()) {
        throw new Error('ImageCapture route requires a redirectTo query parameter.');
      }

      if (!redirectTo.startsWith('/')) {
        throw new Error('ImageCapture redirectTo must be a relative path starting with "/".');
      }

      return {
        redirectTo,
      };
    },
    meta: {
      title: 'Image Capture',
      layout: 'BlankLayout',
      requiresAuth: true,
    },
  },
  ...(import.meta.env.DEV
    ? [
        {
          path: '/test',
          name: 'test',
          component: () => import('@/views/test/DevTest.vue'),
          meta: {
            title: 'Development Tests',
            backNavigation: 'route-list',
          },
        },
        {
          path: '/test/api',
          name: 'test-api',
          component: () => import('@/views/test/ApiTest.vue'),
          meta: {
            title: 'API Test',
            requiresAuth: true,
          },
        },
        {
          path: '/test/api-status',
          name: 'test-api-status',
          component: () => import('@/views/test/ApiStatus.vue'),
          meta: {
            title: 'API Status',
            requiresAuth: true,
          },
        },
      ]
    : []),
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      return '/';
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach(async (to, _from, next) => {
  const title = to.meta.title as string | undefined;
  if (title) {
    document.title = formatPageTitle(title);
  }

  // Handle UUID in route params (auth-check, auth-verify routes)
  // This happens EARLY in the navigation flow to ensure UUID is stored before auth checks
  // This ensures UUID is available even if login flow doesn't complete
  // Priority: Always store UUID from route params if present (newest source of truth)
  if (to.params.uuid && typeof to.params.uuid === 'string') {
    const routeUuid = to.params.uuid;
    const { getUserGuid, setUserGuid } = useStoredUserGuid();
    const storedGuid = await getUserGuid();

    // Always update stored UUID if route has a different UUID (route is source of truth)
    if (storedGuid && storedGuid !== routeUuid) {
      console.warn('[Router] New UUID detected, updating stored GUID:', routeUuid);
      await setUserGuid(routeUuid);
    } else if (!storedGuid) {
      // No stored GUID, store the one from route
      await setUserGuid(routeUuid);
    }
  }

  // Redirect home to route-list only if authenticated
  if (to.path === '/' || to.name === 'home') {
    // Check authentication first
    let isAuthenticated = false;
    const scope = effectScope();
    try {
      await scope.run(async () => {
        const { state: authState, actions } = useAuth();
        if (!authState.user.value) {
          await actions.fetchUserProfile();
        }
        isAuthenticated = !!authState.user.value;
      });
    } catch {
      // Not authenticated, allow home to show
    } finally {
      scope.stop();
    }

    if (isAuthenticated) {
      next({ name: 'route-list' });
      return;
    }
    // If not authenticated, allow home to show (don't redirect)
    next();
    return;
  }

  if (!to.meta.requiresAuth) {
    next();
    return;
  }

  // Check authentication for protected routes
  let isAuthenticated = false;
  const scope = effectScope();
  try {
    await scope.run(async () => {
      const { state: authState, actions } = useAuth();
      if (!authState.user.value) {
        await actions.fetchUserProfile();
      }
      isAuthenticated = !!authState.user.value;
    });
  } catch (error) {
    console.error('[Router] Authentication check failed', error);
  } finally {
    scope.stop();
  }

  // If not authenticated, redirect to OTP verification with stored UUID
  if (!isAuthenticated) {
    const { getUserGuid } = useStoredUserGuid();
    const storedGuid = await getUserGuid();
    if (storedGuid) {
      // Redirect to OTP verification with stored UUID
      next({ name: 'auth-verify', params: { uuid: storedGuid } });
    } else {
      // No stored UUID, redirect to opt-in page for new users
      next({ name: 'opt-in' });
    }
    return;
  }

  next();
});

export default router;
